import React, { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMoon, faSync } from "@fortawesome/free-solid-svg-icons"
import { useGroups } from "../../../hooks/groups"
import {
	fetchPiquet,
	forcerPiquet,
	revenirCalendrierPiquet,
} from "../../../store/actions/depannageDispatchActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"

// Interrupteur du dispatch de piquet (GE-47) : hors horaires (week-end,
// fériés, 17h30–08h00), les dépanneurs créent eux-mêmes leurs missions
// depuis le mobile. Le dispatch peut forcer ouvert / fermé — le forçage
// expire tout seul à la prochaine bascule du calendrier. Fermer / revenir au
// calendrier : groupe `employe` + responsables (décision Jordan D3). Un
// dépanneur peut aussi OUVRIR depuis le mobile : poll pour le voir ici.
const POLL_MS = 60 * 1000
const GROUPES_FERMETURE = [
	"employe",
	"responsable_depannage",
	"admin_depannage",
]
// A14 (CDC 16.10) : OUVRIR = dépanneurs (`employe`) et boss, comme le serveur
// (_exiger_depanneur, D2 03.10) — le bouton n'est plus montré à qui prend 403.
const GROUPES_OUVERTURE = ["employe"]

export const libellePiquet = (etat) => {
	const statut = etat.ouvert ? "Piquet ouvert" : "Piquet fermé"
	if (etat.source === "forcage")
		return `${statut} · forcé${
			etat.forcage_par ? ` par ${etat.forcage_par}` : ""
		} jusqu'au ${etat.jusqu_a}`
	return `${statut} · selon calendrier (jusqu'au ${etat.jusqu_a})`
}

const DepPiquetInterrupteur = () => {
	const dispatch = useDispatch()
	const { hasGroup } = useGroups()
	const peutFermer = hasGroup(GROUPES_FERMETURE)
	const peutOuvrir = hasGroup(GROUPES_OUVERTURE)
	const [etat, setEtat] = useState(null)
	const [busy, setBusy] = useState(false)
	// Pas de setState après démontage (réponse d'un poll ou d'un clic arrivée
	// après la sortie de la page).
	const monte = useRef(true)
	useEffect(
		() => () => {
			monte.current = false
		},
		[],
	)

	const charger = useCallback(
		() =>
			dispatch(fetchPiquet()).then((data) => {
				if (data && monte.current) setEtat(data)
			}),
		[dispatch],
	)

	useEffect(() => {
		charger()
		const id = setInterval(charger, POLL_MS)
		return () => clearInterval(id)
	}, [charger])

	const agir = (thunk) => {
		setBusy(true)
		dispatch(thunk).then((data) => {
			if (!monte.current) return
			if (data) setEtat(data)
			setBusy(false)
		})
	}

	if (etat === null) return null

	const couleur = etat.ouvert ? COLORS.success : COLORS.textMuted
	return (
		<div style={st.bandeau}>
			<span style={{ ...st.statut, color: couleur }}>
				<FontAwesomeIcon icon={busy ? faSync : faMoon} spin={busy} />
				{libellePiquet(etat)}
			</span>
			<div style={st.actions}>
				{peutOuvrir && !etat.ouvert && (
					<button
						type="button"
						style={gs.btnSecondary}
						disabled={busy}
						onClick={() => agir(forcerPiquet(true))}
					>
						Forcer ouvert
					</button>
				)}
				{peutFermer && etat.ouvert && (
					<button
						type="button"
						style={gs.btnSecondary}
						disabled={busy}
						onClick={() => agir(forcerPiquet(false))}
					>
						Forcer fermé
					</button>
				)}
				{peutFermer && etat.source === "forcage" && (
					<button
						type="button"
						style={gs.btnSecondary}
						disabled={busy}
						onClick={() => agir(revenirCalendrierPiquet())}
					>
						Revenir au calendrier
					</button>
				)}
			</div>
		</div>
	)
}

const st = {
	bandeau: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 12,
		flexWrap: "wrap",
		padding: "10px 16px",
		marginTop: 16,
		borderRadius: 12,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
	},
	statut: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		fontSize: 14,
		fontWeight: 600,
	},
	actions: { display: "flex", gap: 8 },
}

export default DepPiquetInterrupteur
